import { useState } from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import {
  Dashboard as DashboardIcon,
  ShoppingCart as PedidosIcon,
  People as ClientesIcon,
  Event as VisitasIcon,
  TrendingUp as LeadsIcon,
  Assignment as AtividadesIcon,
  Map as MapaIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  PhotoLibrary as FotosIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { NotificationPermissionBanner } from '../NotificationPermissionBanner';
import { SyncStatusIndicator } from '../sync/SyncStatusIndicator';

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: 72
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2),
  ...theme.mixins.toolbar
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

// AIDEV-NOTE: AppBar só desloca no desktop (md+) quando drawer está aberto
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    [theme.breakpoints.up('md')]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`
    },
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }),
  backgroundColor: theme.palette.primary.main
}));

// AIDEV-NOTE: Drawer permanente collapsível — usado apenas no desktop (md+)
const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}));

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const baseNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { label: 'Pedidos', path: '/pedidos', icon: <PedidosIcon /> },
  { label: 'Clientes', path: '/clientes', icon: <ClientesIcon /> },
  { label: 'Visitas', path: '/visitas', icon: <VisitasIcon /> },
  { label: 'Mapa', path: '/mapa', icon: <MapaIcon /> },
  { label: 'Leads', path: '/leads', icon: <LeadsIcon /> },
  { label: 'Atividades', path: '/atividades', icon: <AtividadesIcon /> }
];

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // AIDEV-NOTE: isMobile detecta telas < md (768px) para alternar entre drawer temporary/permanent
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // AIDEV-NOTE: Itens extras visiveis apenas para admin
  const navItems: NavItem[] =
    user?.perfil === 'admin'
      ? [
          ...baseNavItems,
          { label: 'Produtos', path: '/admin/catalogo', icon: <FotosIcon /> },
          { label: 'Configurações', path: '/admin/usuarios', icon: <SettingsIcon /> }
        ]
      : baseNavItems;

  // AIDEV-NOTE: Desktop inicia fechado (ícones apenas) — mobile sempre fechado
  const [open, setOpen] = useState(false);

  const handleDrawerToggle = () => setOpen(!open);
  const handleDrawerClose = () => setOpen(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    // AIDEV-NOTE: Fecha o drawer automaticamente ao navegar no mobile
    if (isMobile) setOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // AIDEV-NOTE: Conteúdo do drawer compartilhado entre mobile (temporary) e desktop (permanent)
  const drawerContent = (
    <>
      <DrawerHeader>
        <Typography
          variant="h6"
          sx={{
            color: 'primary.main',
            fontWeight: 'bold',
            display: open ? 'block' : 'none'
          }}
        >
          Menu
        </Typography>
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: 'text.secondary' }}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </DrawerHeader>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              sx={{
                minHeight: 48,
                justifyContent: open || isMobile ? 'initial' : 'center',
                px: 2.5
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || isMobile ? 3 : 'auto',
                  justifyContent: 'center',
                  color: 'primary.main'
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  opacity: open || isMobile ? 1 : 0,
                  '& .MuiTypography-root': { fontSize: '0.875rem' }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 48,
              justifyContent: open || isMobile ? 'initial' : 'center',
              px: 2.5
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open || isMobile ? 3 : 'auto',
                justifyContent: 'center',
                color: 'error.main'
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Sair"
              sx={{
                opacity: open || isMobile ? 1 : 0,
                '& .MuiTypography-root': { fontSize: '0.875rem', color: 'error.main' }
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={!isMobile && open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            AzoupVendas
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* AIDEV-NOTE: so aparece quando ha algo a dizer (offline, fila ou falha) — com tudo
                em dia a Toolbar fica identica a de antes. Ver SyncStatusIndicator. */}
            <SyncStatusIndicator />
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }} src={undefined}>
              {user?.nome?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
              {user?.nome || 'Usuário'}
            </Typography>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* AIDEV-NOTE: Mobile — drawer temporary (overlay, não empurra conteúdo) */}
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' }
          }}
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        /* AIDEV-NOTE: Desktop — drawer permanent collapsível (empurra conteúdo) */
        <DesktopDrawer variant="permanent" open={open}>
          {drawerContent}
        </DesktopDrawer>
      )}

      {/* AIDEV-NOTE: height usa 100svh no mobile (small viewport = menor valor possivel com chrome do browser
          visivel), evitando overflow causado pelo calculo incorreto de 100vh em browsers mobile.
          No desktop 100vh e confiavel. 100svh foi escolhido sobre 100dvh para evitar resize ao
          esconder/mostrar a barra do browser durante scroll — mantendo o layout estavelmente fixo.
          display: flex + flexDirection: column permite que filhos com flex: 1 preencham a altura restante. */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 0, md: 3 },
          mt: 8,
          backgroundColor: theme.palette.grey[50],
          height: { xs: 'calc(100svh - 64px)', md: 'calc(100vh - 64px)' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          // AIDEV-NOTE: No mobile o drawer é temporary (overlay) — conteúdo ocupa 100% da largura
          width: { xs: '100%', md: `calc(100% - ${!open ? 72 : drawerWidth}px)` },
          minWidth: 0
        }}
      >
        <NotificationPermissionBanner />
        {children}
      </Box>
    </Box>
  );
}
